import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap';
import { toast } from 'react-toastify';
import PageHeader from '../common/PageHeader';
import ActionConfirmModal from '../common/ActionConfirmModal';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import MedDataTable from '../common/MedDataTable';
import apiBase from '../../apiConfig';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';

const initialForm = {
  name: '',
  brand_text: '',
  logo: '',
  color: '#2c7be5',
  background: '#ffffff',
  title_color: '#102a43',
  is_active: 1
};

const Filials = () => {
  const [filials, setFilials] = useState([]);
  const [pageSize, setPageSize] = useStoredTablePageSize();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchFilials = async () => {
    const res = await axios.get(`${apiBase}/filials.php`);
    if (res.data.success) {
      setFilials(res.data.filials);
    }
  };

  useEffect(() => {
    fetchFilials().catch(console.error);
  }, []);

  const columns = [
    { dataField: 'name', text: 'Filial', sort: true, classes: 'align-middle font-weight-bold' },
    {
      dataField: 'color',
      text: 'Rang',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge style={{ backgroundColor: value }}>{value}</Badge>
    },
    {
      dataField: 'background',
      text: 'Fon',
      sort: true,
      classes: 'align-middle',
      formatter: (value, row) => <Badge style={{ backgroundColor: value, color: row.title_color }}>{value}</Badge>
    },
    {
      dataField: 'is_active',
      text: 'Holat',
      sort: true,
      classes: 'align-middle',
      formatter: value => <Badge color={value ? 'soft-success' : 'soft-secondary'}>{value ? 'Faol' : 'Nofaol'}</Badge>
    },
    {
      dataField: 'actions',
      text: 'Amallar',
      isDummyField: true,
      headerStyle: { width: '6.5rem' },
      classes: 'align-middle',
      formatter: (data, item) => (
        <div className="med-actions-row">
          <Button color="link" className="p-0 med-action-btn" onClick={() => openEdit(item)}>
            <FontAwesomeIcon icon="pencil-alt" />
          </Button>
          <Button
            color="link"
            className="p-0 text-danger med-action-btn med-action-btn-danger"
            onClick={() => setDeleteTarget({ id: item.id, name: item.name })}
          >
            <FontAwesomeIcon icon="trash-alt" />
          </Button>
        </div>
      )
    }
  ];

  const openCreate = () => {
    setEditingId(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEdit = filial => {
    setEditingId(filial.id);
    setForm({
      name: filial.name,
      brand_text: filial.brand_text || '',
      logo: filial.logo || '',
      color: filial.color || '#2c7be5',
      background: filial.background || '#ffffff',
      title_color: filial.title_color || '#102a43',
      is_active: filial.is_active ? 1 : 0
    });
    setModalOpen(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      if (editingId) {
        const res = await axios.put(`${apiBase}/filials.php`, { ...form, id: editingId });
        if (res.data.success) toast.success(res.data.message);
      } else {
        const res = await axios.post(`${apiBase}/filials.php`, form);
        if (res.data.success) toast.success(res.data.message);
      }

      setModalOpen(false);
      fetchFilials();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Filial saqlanmadi');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await axios.delete(`${apiBase}/filials.php`, { data: { id: deleteTarget.id } });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchFilials();
        setDeleteTarget(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Filial o‘chirilmadi');
    }
  };

  return (
    <>
      <PageHeader title="Filiallar" description="Filiallar, ranglar va faol holatlarni boshqarish." className="mb-3" />
      <Card className="border-0 shadow-sm med-dashboard-card">
        <CardBody className="med-dashboard-card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Filiallar ro'yxati</h5>
            <div className="d-flex align-items-center">
              <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size mr-3" />
              <Button color="outline-primary" className="med-outline-add-btn" onClick={openCreate}>
                <FontAwesomeIcon icon="plus" className="mr-2" />
                Yangi filial
              </Button>
            </div>
          </div>
          <MedDataTable
            data={filials}
            columns={columns}
            pageSize={pageSize}
            paginationResetKey="filials"
            noDataIndication={<div className="text-center py-4 text-500">Filiallar topilmadi</div>}
          />
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} centered>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          {editingId ? 'Filialni tahrirlash' : 'Yangi filial'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label>Filial nomi</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Firma nomi / rekvizit satri</Label>
              <Input
                value={form.brand_text}
                onChange={e => setForm({ ...form, brand_text: e.target.value })}
                placeholder="Masalan: Suvoriy Medical Center"
              />
            </FormGroup>
            <FormGroup>
              <Label>Logo fayl nomi yoki yo'li</Label>
              <Input
                value={form.logo}
                onChange={e => setForm({ ...form, logo: e.target.value })}
                placeholder="Masalan: filial-logo.png"
              />
              <div className="fs--2 text-500 mt-2">Eski tizim yo'li: `userfiles/images/filial/` ichidagi fayl nomini kiriting.</div>
            </FormGroup>
            <FormGroup>
              <Label>Asosiy rang</Label>
              <Input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Fon rangi</Label>
              <Input type="color" value={form.background} onChange={e => setForm({ ...form, background: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Sarlavha rangi</Label>
              <Input type="color" value={form.title_color} onChange={e => setForm({ ...form, title_color: e.target.value })} />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              <FontAwesomeIcon icon="times" className="mr-2" />
              Bekor qilish
            </Button>
            <Button color="primary" type="submit">
              <FontAwesomeIcon icon="check-circle" className="mr-2" />
              Saqlash
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ActionConfirmModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        title="Filialni nofaol qilish"
        message={deleteTarget ? `${deleteTarget.name} filialini nofaol qilishni tasdiqlaysizmi?` : ''}
        confirmLabel="Nofaol qilish"
        onConfirm={handleDelete}
      />
    </>
  );
};

export default Filials;
